import React, { Component } from "react";
import request from "../utils/request";
import moment from "moment";

import JackpotElements from "./JackpotElements"

// Multi-event handoff (bump-jackpot-screen#4):
// When the /jackpot/:id API response has event_ids with length >= 2, the
// screen is a multi-event jackpot. Multi-event rendering lives in
// next-gen-dashboard at /kiosk-multi (accepts ?jackpotId=N&eventIds=X,Y),
// which is deployed to the same CloudFront distributions this app serves
// from (/kiosk-multi is not overridden and falls through to next-gen-dashboard).
// We hand off via window.location.replace and return early. If event_ids is
// missing/null/empty/length-1 the single-event flow proceeds unchanged.
//
// Query params are URL-encoded per element (event ids joined with a literal
// "," AFTER encoding — the destination /kiosk-multi splits on "," and coerces
// each segment via Number()). This is defense-in-depth: the API returns
// integer PKs today (Eloquent BIGINT + 'event_ids' => 'array' cast) so
// encoding is a no-op on the happy path, but guards against schema drift or
// non-numeric ids in the future.
function redirectIfMultiEvent(jackpotMeta) {
  if (
    jackpotMeta &&
    Array.isArray(jackpotMeta.event_ids) &&
    jackpotMeta.event_ids.length >= 2
  ) {
    const jackpotId = encodeURIComponent(String(jackpotMeta.id));
    const eventIds = jackpotMeta.event_ids
      .map((id) => encodeURIComponent(String(id)))
      .join(",");
    window.location.replace(
      `/kiosk-multi?jackpotId=${jackpotId}&eventIds=${eventIds}`
    );
    return true;
  }
  return false;
}

// Function to convert a string from snake_case or kebab-case to camelCase
function toCamelCase(str) {
  // The regular expression /([-_][a-z])/g matches any underscore or hyphen followed by a lowercase letter
  // The replace function changes these to the uppercase version of the letter
  return str.replace(/([-_][a-z])/g, g => g[1].toUpperCase());
}

// Function to convert all keys in an object from snake_case or kebab-case to camelCase
function convertKeysToCamelCase(obj) {
  // If the input is a string, try to parse it as JSON
  if (typeof obj === 'string') {
      try {
          obj = JSON.parse(obj);
      } catch (e) {
          // If parsing fails, return the original string
          return obj;
      }
  }

  // If the input is an array, convert all of its elements
  if (Array.isArray(obj)) {
      return obj.map(v => convertKeysToCamelCase(v));
  }
  // If the input is an object, convert all of its keys
  else if (obj !== null && obj.constructor === Object) {
      return Object.keys(obj).reduce(
          (result, key) => ({
              // Use the spread operator to add the previous keys to the new object
              // Convert the current key and add it to the new object
              ...result,
              [toCamelCase(key)]: convertKeysToCamelCase(obj[key])
          }),
          {}
      );
  }
  // If the input is neither a string, an array, nor an object, return it as is
  return obj;
}

function extractUrlValues(value) {
  if (!value || typeof value !== "string") return [];
  const matches = [];
  const regex = /url\((.*?)\)/gi;
  let current;
  while ((current = regex.exec(value)) !== null) {
    if (current[1]) {
      const cleaned = current[1].trim().replace(/^['"]|['"]$/g, "");
      if (cleaned) matches.push(cleaned);
    }
  }
  return matches;
}

function preloadImage(url) {
  if (!url) return Promise.resolve();
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = resolve;
    image.onerror = resolve;
    image.src = url;
  });
}

function preloadBackgroundImages(css) {
  if (!css || typeof css !== "object") return Promise.resolve();
  const urls = [];
  [css.backgroundImage]
    .forEach((value) => {
      if (value) extractUrlValues(value).forEach((url) => urls.push(url));
    });
  if (urls.length === 0) return Promise.resolve();
  return Promise.all(urls.map((url) => preloadImage(url)));
}



class JackpotKiosk extends Component {
  constructor(props) {
    super(props);
    this.state = {
      background: {},
      elements: [],
      id: -1,
      eventId: 0,
      jackpot_localized: "0",
      prize_localized: "0",
      winner: "",
      has_addon: false,
      addon_jackpot_localized: "0",
      addon_prize_localized: "0",
      addon_winner: "",
      pollInterval: false,
      active: 1,
      dataSrc: "",
      initialized: false,
      jackpotReload: 0,
      jackpotIdFromUrl: 0,
      eventIdFromUrl: 0,
      defaultJackpotId: props.defaultJackpotId ? props.defaultJackpotId : 0,
      lastUpdatedAt: null, // Track last updated_at timestamp
      RaffleEvent: {
        Description: "",
        GrossSales: "",
        RaffleDate: "",
        WinAmount: "",
        WinTicket: "",
        CTAPrize: "",
        CTACard: "",
        CTAEnvelope: ""
      }
    }
  };

  getLoaderElement = () => document.getElementById("loader-wrapper");

  showLoaderOverlay = () => {
    const loader = this.getLoaderElement();
    if (!loader) return;
    loader.classList.remove("bump-loader-hidden");
    loader.classList.add("bump-loader-visible");
    document.documentElement.style.setProperty("--bump-loader-bootstrap-visibility", "visible");
    document.documentElement.style.setProperty("--bump-loader-bootstrap-opacity", "1");
    document.documentElement.style.setProperty("--bump-loader-bootstrap-z-index", "9999");
  }

  hideLoaderOverlay = (delay = 0) => {
    const hide = () => {
      const loader = this.getLoaderElement();
      if (!loader) return;
      loader.classList.remove("bump-loader-visible");
      loader.classList.add("bump-loader-hidden");
      document.documentElement.style.setProperty("--bump-loader-bootstrap-opacity", "0");
      document.documentElement.style.setProperty("--bump-loader-bootstrap-visibility", "hidden");
      document.documentElement.style.setProperty("--bump-loader-bootstrap-z-index", "-1");
    };

    if (delay > 0) {
      window.setTimeout(hide, delay);
      return;
    }

    hide();
  }

  componentDidMount = () => {
    this.getUrlParams();
  };
  componentDidUpdate = () => {
    if (!this.state.initialized) {
      this.setState({ initialized: true });
      if (+this.state.jackpotIdFromUrl !== -1) this.getData();
    }
  };
  // Strip query params from the url
  getUrlParams = () => {
    let urlParams = this.props.location.search;
    let params = {};
    let parts = urlParams.replace(/[?&]+([^=&]+)=([^&]*)/gi,
      function (m, key, value) {
        params[key] = value;
      });
    let jackpotIdFromUrl = params['jackpotId'];
    let eventIdFromUrl = params['eventId'];
    if (+jackpotIdFromUrl === -1) {
      this.fetchJackpotScreens(this.state.defaultJackpotId);
    }
    this.setState({
      jackpotIdFromUrl: jackpotIdFromUrl,
      eventIdFromUrl: eventIdFromUrl
    }, () => this.getData());
  }
  fetchJackpotScreens = (screenId) => {
    this.showLoaderOverlay();
    request.get(this, `/jackpot/${screenId}`).then(jackpot => {
      // Multi-event handoff (see redirectIfMultiEvent above). Do NOT call
      // hideLoaderOverlay before the redirect — the browser tears down the page.
      if (redirectIfMultiEvent(jackpot)) return;
      const parsedCss = convertKeysToCamelCase(JSON.parse(jackpot.css));
      const parsedElements = convertKeysToCamelCase(JSON.parse(jackpot.elements));
      preloadBackgroundImages(parsedCss).then(() => {
        this.setState({
          background: parsedCss,
          elements: parsedElements,
          id: jackpot.id,
          active: +jackpot.active === 1 ? true : false,
          dataSrc: jackpot.data_src
        }, () => {
          this.hideLoaderOverlay(120);
          this.getExternalData();
        });
      });
    }).catch(() => this.hideLoaderOverlay(0));
  }
  getExternalData = () => {
    let self = this;
    self.setState({ pollInterval: false }, clearInterval(this.state.pollInterval));
    
    // Guard: dataSrc must be set before fetching
    if (!this.state.dataSrc) {
      return Promise.resolve();
    }
    
    let externalData = this.state.RaffleEvent;
    return request.getXML(this, `${this.state.dataSrc}`).then(resp => {
      let responseData = resp['RaffleEvent'];

      if (responseData['Description']) {
        externalData.Description = responseData['Description'] ? responseData['Description'][0] : "";
        externalData.GrossSales = responseData['GrossSales'] ? responseData['GrossSales'][0] : "";
        externalData.RaffleDate = responseData['RaffleDate'] ? responseData['RaffleDate'][0] : "";
        externalData.WinAmount = responseData['WinAmount'] ? responseData['WinAmount'][0] : "";
        externalData.WinTicket = responseData['WinTicket'] ? responseData['WinTicket'][0] : "";
      }
      else { // this supports locations with multiple
        Object.values(responseData).forEach(raffle => {

          if (raffle[0] !== '' &&  raffle[0]["IsActiveDisplay"] && raffle[0]['IsActiveDisplay'][0] === "1") {
            externalData.Description = raffle[0]['Description'] ? raffle[0]['Description'][0] : "";
            externalData.GrossSales = raffle[0]['GrossSales'] ? raffle[0]['GrossSales'][0] : "";
            externalData.RaffleDate = raffle[0]['RaffleDate'] ? raffle[0]['RaffleDate'][0] : "";
            externalData.WinAmount = raffle[0]['WinAmount'] ? raffle[0]['WinAmount'][0] : "";
            externalData.WinTicket = raffle[0]['WinTicket'] ? raffle[0]['WinTicket'][0] : "";
            return;
          } else if (raffle[0] !== '' && raffle[0]['CTAPrize'] ) {
            externalData.Description = raffle[0]['Description'] ? raffle[0]['Description'][0] : "";
            externalData.GrossSales = raffle[0]['GrossSales'] ? raffle[0]['GrossSales'][0] : "";
            externalData.RaffleDate = raffle[0]['RaffleDate'] ? raffle[0]['RaffleDate'][0] : "";
            externalData.WinAmount = raffle[0]['WinAmount'] ? raffle[0]['WinAmount'][0] : "";
            externalData.WinTicket = raffle[0]['WinTicket'] ? raffle[0]['WinTicket'][0] : "";
            externalData.CTAPrize = raffle[0]['CTAPrize'] ? raffle[0]['CTAPrize'][0] : "";
            externalData.CTACard = raffle[0]['CTACard'] ? raffle[0]['CTACard'][0] : "";
            externalData.CTAEnvelope = raffle[0]['CTAEnvelope'] ? raffle[0]['CTAEnvelope'][0] : "";
            return;

          } else if (raffle[0] !== '' && raffle[0]['Description'] )  {

            externalData.Description = raffle[0]['Description'] ? raffle[0]['Description'][0]  : "";
            externalData.GrossSales = raffle[0]['GrossSales'] ? raffle[0]['GrossSales'][0] : "";
            externalData.RaffleDate = raffle[0]['RaffleDate'] ?raffle[0]['RaffleDate'][0] : "";
            externalData.WinAmount = raffle[0]['WinAmount'] ? raffle[0]['WinAmount'][0] : "";
            externalData.WinTicket = raffle[0]['WinTicket'] ? raffle[0]['WinTicket'][0] : "";
            return;
          }

        });
      }

      this.setState({ RaffleEvent: externalData });
    }).then(() => {
      self.handlePoll();
    });
  }
  getData = () => {
    if (+this.state.jackpotIdFromUrl === -1) return;
    let self = this;
    self.setState({ pollInterval: false }, clearInterval(this.state.pollInterval));
    // Check for the post url w.r.t url param "eventId"
    let newUrl = (this.state.eventIdFromUrl) ? `web/event/${this.state.eventIdFromUrl}?persist_addons=1` : 'web/jackpot-event';
    let jackpotId = (this.state.jackpotIdFromUrl) ? this.state.jackpotIdFromUrl : null;
    return request
      .list(this, '/' + newUrl)
      .then(resp => {
        if (resp.status === "error") return resp; // if error, then exit
        // Check for response
        let response = (this.state.eventIdFromUrl) ? resp : resp[0];
        let addon = response.addons.length > 0 ? response.addons[0] : false;

        // Check to see if reload required
        if (this.state.jackpotReload != 0 && response.jackpot_reload > this.state.jackpotReload) {
          window.location.reload();
        }

        // Store event and addon data as before
        this.setState({
          eventId: response.id > 0 ? response.id : 0,
          jackpot_localized: response.jackpot_localized,
          prize_localized: response.prize_localized,
          draw_date: moment(response.end_at).format("YYYY-MM-DD"),
          winner: response.draws[0].number,
          jackpotReload: response.jackpot_reload,
          addon_jackpot_localized: addon ? addon.jackpot_localized : '',
          addon_prize_localized: addon ? addon.prize_localized : '',
          addon_draw_date: addon ? moment(addon.end_at).format("YYYY-MM-DD") : '',
          addon_winner: addon ? addon.draws[0].number : null,
          has_addon: addon ? true : false
        }, () => {
          // Precedence: pinned URL id -> event's jackpot_screen -> tenant
          // default -> /jackpot/0. Redirect is skipped on the /jackpot/0
          // fallback so a stray multi-event screen can't hijack it (BCBN-2887).
          const hasPinnedScreenId = jackpotId !== null && jackpotId !== undefined && String(jackpotId).trim() !== "";
          const eventScreenId = hasPinnedScreenId ? jackpotId : response.jackpot_screen;
          const resolvedScreenId = (eventScreenId !== null && eventScreenId !== undefined && +eventScreenId > 0)
            ? eventScreenId
            : this.state.defaultJackpotId;

          const hasResolvedScreen = resolvedScreenId !== null && resolvedScreenId !== undefined
            && String(resolvedScreenId).trim() !== "" && +resolvedScreenId > 0;
          const isLastResortFallback = !hasResolvedScreen;
          const jId = hasResolvedScreen ? resolvedScreenId : 0;

          request.get(this, `/jackpot/${jId}`).then(jackpotMeta => {
            if (!isLastResortFallback && redirectIfMultiEvent(jackpotMeta)) return;
            // Early guard: if jackpotMeta is falsy or missing updated_at, hide loader and return
            if (!jackpotMeta || !jackpotMeta.updated_at) {
              this.hideLoaderOverlay(0);
              return;
            }
            const currentScreenId = Number(this.state.id);
            const nextScreenId = Number(jId);
            const hasValidIds = !Number.isNaN(currentScreenId) && !Number.isNaN(nextScreenId);
            const hasScreenChanged = hasValidIds ? currentScreenId !== nextScreenId : String(this.state.id) !== String(jId);
            const newUpdatedAt = jackpotMeta.updated_at;
            if (hasScreenChanged || this.state.lastUpdatedAt !== newUpdatedAt) {
              // Only fetch and update if updated_at changed
              const shouldShowLoader = this.state.id === -1;
              if (shouldShowLoader) this.showLoaderOverlay();
              try {
                const parsedCss = convertKeysToCamelCase(JSON.parse(jackpotMeta.css));
                const parsedElements = convertKeysToCamelCase(JSON.parse(jackpotMeta.elements));
                preloadBackgroundImages(parsedCss).then(() => {
                  this.setState({
                    background: parsedCss,
                    elements: parsedElements,
                    id: jackpotMeta.id,
                    active: +jackpotMeta.active === 1 ? true : false,
                    dataSrc: jackpotMeta.data_src,
                    lastUpdatedAt: newUpdatedAt
                  }, () => this.hideLoaderOverlay(shouldShowLoader ? 120 : 0));
                });
              } catch (e) {
                this.hideLoaderOverlay(0);
              }
            } else {
              this.hideLoaderOverlay(0);
            }
          });
        })
      })
      .then(callbackResponse => {
        self.handlePoll(); // poll every 5 seconds
      });
  };
  handlePoll = () => {
    let recurringFunction = +this.state.jackpotIdFromUrl === -1 ? this.getExternalData : this.getData;
    if (!this.state.pollInterval) {
      let poll = setInterval(recurringFunction, 5000);
      this.setState({
        pollInterval: poll
      });
    }
  }
  render() {
    const { background, elements, jackpot_localized, prize_localized, winner, RaffleEvent, jackpotIdFromUrl, draw_date, addon_jackpot_localized, addon_prize_localized, addon_winner, addon_draw_date, has_addon } = this.state;
    let newElements = [];

    elements.map((v, k) => {
      let pusher = Object.assign({}, v);
      if (+jackpotIdFromUrl === -1) {
        /* XML Feed Jackpot Elements */
        if (v.label === "Raffle Date" || v.id === 'raffleDate') pusher.content = RaffleEvent.RaffleDate ? RaffleEvent.RaffleDate : '';
        if (v.label === "Text" || v.id === 'text') pusher.content = RaffleEvent.Description ? RaffleEvent.Description : '';
        if (v.label === "Jackpot" || v.id === 'jackpot') pusher.content = RaffleEvent.CTAPrize ? RaffleEvent.CTAPrize :  (RaffleEvent.GrossSales ? RaffleEvent.GrossSales : '');
        if (v.label === "Prize" || v.id === 'prize') pusher.content = RaffleEvent.WinAmount ? RaffleEvent.WinAmount : '';
        if (v.label === "Winning Number" || v.id === 'winningNumber') pusher.content = RaffleEvent.WinTicket ? RaffleEvent.WinTicket : '';
        if (v.label === "Weekly Jackpot" || v.id === 'weeklyJackpot') pusher.content = RaffleEvent.WinAmount ? RaffleEvent.WinAmount : '';
        if ((v.label === "Winning Number" || v.id === 'winningNumber') && (RaffleEvent.WinTicket === "" || RaffleEvent.WinTicket === null) && v.hideCaptionUntilDraw) return true;
      } else {
        if (v.label === "Jackpot" || v.id === 'jackpot') pusher.content = jackpot_localized ? jackpot_localized : '';
        if (v.label === "Prize" || v.id === 'prize') pusher.content = prize_localized ? prize_localized : '';
        if (v.label === "Weekly Jackpot" || v.id === 'weeklyJackpot') pusher.content = prize_localized ? prize_localized : '';
        if (v.label === "Winning Number" || v.id === 'winningNumber') pusher.content = winner;
        if (v.label === "Raffle Date" || v.id === 'raffleDate') pusher.content = draw_date ? draw_date : '';
        if ((v.label === "Winning Number" || v.id === 'winningNumber') && (winner === "" || winner === null) && v.hideCaptionUntilDraw) return true;

        if (v.label.startsWith("Addon ") || (v.id && v.id.startsWith("addon"))) {
          if (!has_addon) {
            // only display these elements if event has an addon
            return true;
          }

          if (v.label === "Addon Jackpot" || v.id === 'addonJackpot') pusher.content = addon_jackpot_localized ? addon_jackpot_localized : '';
          if (v.label === "Addon Prize" || v.id === 'addonPrize') pusher.content = addon_prize_localized ? addon_prize_localized : '';
          if (v.label === "Addon Raffle Date" || v.id === 'addonRaffleDate') pusher.content = addon_draw_date ? addon_draw_date : '';
          if (v.label === "Addon Winning Number" || v.id === 'addonWinningNumber') pusher.content = addon_winner;
          if ((v.label === "Addon Winning Number" || v.id === 'addonWinningNumber') && (addon_winner === "" || addon_winner === null) && v.hideCaptionUntilDraw) return true;
        }
      }
      return newElements.push(pusher);
    });

    return (
      <JackpotElements background={background} elements={newElements} />
    );
  }
}

export default JackpotKiosk;
